// Impersonation-v1 (2026-06-21) — sticky red bar shown on every page
// of the impersonation tab.
//
// Mounted inside <BrowserRouter> in App.js so it can appear on any
// route, including /login (in case the impersonator opens a fresh tab
// while the impersonation token is already in localStorage). The body
// gets a top padding shim equal to the banner height so the rest of
// the UI isn't obscured.
//
// "Exit" → fire-and-forget POST /api/me/impersonation-end for the
// audit trail, clear the impersonation token from localStorage, then
// hard-redirect to /admin. We hard-redirect rather than navigate(...)
// so AuthContext re-initialises and any in-memory caches built under
// the impersonated identity are dropped.
import { useEffect } from "react";
import axios from "axios";
import { AlertTriangle, X } from "lucide-react";
import {
  isImpersonating,
  getImpersonationMeta,
  clearImpersonation,
} from "@/lib/impersonation";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const BANNER_HEIGHT_PX = 36;

function roleLabel(role) {
  if (!role) return "User";
  return role.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

export default function ImpersonationBanner() {
  const active = isImpersonating();
  const meta = active ? getImpersonationMeta() : null;
  const target = meta?.target_user || null;

  // Push the rest of the page down by the banner height. Done via a
  // body style mutation rather than wrapping the whole app, because the
  // banner can be mounted/unmounted independently of route changes.
  useEffect(() => {
    if (!active) {
      document.body.style.paddingTop = "";
      return undefined;
    }
    const prev = document.body.style.paddingTop;
    document.body.style.paddingTop = `${BANNER_HEIGHT_PX}px`;
    return () => {
      document.body.style.paddingTop = prev;
    };
  }, [active]);

  if (!active) return null;

  const handleExit = async () => {
    // Best-effort audit row — never block the exit on a network error.
    try {
      await axios.post(`${API}/me/impersonation-end`);
    } catch {
      /* swallow */
    }
    clearImpersonation();
    // Hard reload so AuthContext re-bootstraps with the admin's session
    // token (still in localStorage at 'token'). Targets /admin which
    // bounces unauthenticated visitors to /login — perfect either way.
    window.location.href = "/admin";
  };

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="impersonation-banner"
      style={{ height: BANNER_HEIGHT_PX, lineHeight: `${BANNER_HEIGHT_PX}px` }}
      className="fixed top-0 left-0 right-0 z-[1000] bg-[#991b1b] text-white text-sm px-4 flex items-center justify-between shadow"
    >
      <div className="flex items-center gap-2 truncate">
        <AlertTriangle className="w-4 h-4 shrink-0" />
        <span className="truncate">
          <span className="font-semibold">VIEWING AS:</span>{" "}
          {target?.name || "Unknown"}{" "}
          {target?.email ? (
            <span className="opacity-80">({target.email})</span>
          ) : null}
          {target?.role ? (
            <span className="opacity-80"> &middot; {roleLabel(target.role)}</span>
          ) : null}
        </span>
      </div>
      <button
        type="button"
        onClick={handleExit}
        data-testid="impersonation-exit-btn"
        className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded bg-white/15 hover:bg-white/25 text-white text-xs font-medium shrink-0"
      >
        <X className="w-3.5 h-3.5" />
        Exit impersonation
      </button>
    </div>
  );
}
